import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Download, Save, TrendingUp, Settings2 } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { useNavigate } from "react-router-dom";
import StarterChallenge from "@/components/StarterChallenge";

// Extracted chart style constants to avoid inline-object re-renders each frame.
const CHART_TICK = { fill: "#666", fontSize: 10 };
const CHART_AXIS_LINE = { stroke: "#222" };
const CHART_TOOLTIP_STYLE = { background: "#0A0A0A", border: "1px solid #333", borderRadius: 0, fontSize: 12 };
const CHART_TOOLTIP_LABEL_STYLE = { color: "#999" };

const StatBig = ({ label, value, accent, tid }) => (
  <div data-testid={tid} className="hairline bg-[#0F0F0F] p-5">
    <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">{label}</div>
    <div className={`font-display text-3xl font-black tabular ${accent ? "text-[#39FF14]" : "text-white"}`}>{value}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [today, setToday] = useState(null);
  const [template, setTemplate] = useState(null);
  const [summary, setSummary] = useState({ week: {}, month: {}, quarter: {}, year: {} });
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);
  const [income, setIncome] = useState(0);
  const [notes, setNotes] = useState("");

  const load = async () => {
    const [t, s, h] = await Promise.all([
      api.get("/kpi/today"),
      api.get("/kpi/summary"),
      api.get("/kpi/history?days=14"),
    ]);
    setToday(t.data.entry);
    setTemplate(t.data.template);
    setSummary(s.data);
    setHistory(h.data);
    setIncome(t.data.entry.income || 0);
    setNotes(t.data.entry.notes || "");
  };
  useEffect(() => { load(); }, []);

  const setVal = (key, v) => setToday((prev) => ({
    ...prev, values: { ...(prev.values || {}), [key]: v === "" ? 0 : Number(v) },
  }));

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/kpi/entry", {
        entry_date: today.entry_date,
        values: today.values || {},
        income: Number(income) || 0,
        notes,
      });
      toast.success("KPIs saved");
      load();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const exportReport = async (period) => {
    const { data } = await api.get(`/kpi/export?period=${period}`, { responseType: "text" });
    const blob = new Blob([data], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kpi-${period}-${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`${period} report ready — paste into Google Docs / Sheets`);
  };

  const historyChart = useMemo(() =>
    (history || []).map((e) => {
      const vals = e.values || {};
      let activity = 0, leads = 0, deals = 0;
      Object.entries(vals).forEach(([k, v]) => {
        if (k.endsWith(".actual")) activity += v;
        else if (k.endsWith(".leads")) leads += v;
        else if (k.endsWith(".deals")) deals += v;
      });
      return { entry_date: e.entry_date, activity, leads, deals };
    }), [history]);

  if (!today || !template) return <div className="p-10 text-neutral-500">Loading…</div>;

  const cols = template.track_columns || ["actual", "answers", "leads", "offers", "deals"];
  const week = summary.week || {};

  return (
    <div className="p-8 md:p-10 max-w-[1500px]">
      <StarterChallenge />
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#39FF14] mb-2">// {template.name} · {today.entry_date}</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight">
            Hey {user?.name?.split(" ")[0] || "there"}. <span className="text-neutral-500">Log the numbers.</span>
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            data-testid="edit-template-btn"
            onClick={() => nav("/kpi-template")}
            variant="outline"
            className="rounded-none hairline bg-transparent hover:bg-[#111] hover:text-[#39FF14] text-xs uppercase tracking-widest h-10"
          >
            <Settings2 size={14} className="mr-2" /> Customize
          </Button>
          <Button
            data-testid="export-week-btn"
            onClick={() => exportReport("week")}
            variant="outline"
            className="rounded-none hairline bg-transparent hover:bg-[#111] hover:text-[#39FF14] text-xs uppercase tracking-widest h-10"
          >
            <Download size={14} className="mr-2" /> Week
          </Button>
          <Button
            data-testid="export-month-btn"
            onClick={() => exportReport("month")}
            className="rounded-none bg-[#39FF14] text-black hover:bg-white text-xs uppercase tracking-widest h-10"
          >
            <Download size={14} className="mr-2" /> Month report
          </Button>
        </div>
      </div>

      {/* Roll-ups */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
        <StatBig tid="stat-week-activity" label="Week · Activity" value={Math.round(week.activity || 0)} />
        <StatBig tid="stat-week-leads" label="Week · Leads" value={Math.round(week.leads || 0)} />
        <StatBig tid="stat-week-offers" label="Week · Offers" value={Math.round(week.offers || 0)} />
        <StatBig tid="stat-week-deals" label="Week · Deals" value={Math.round(week.deals || 0)} accent />
        <StatBig tid="stat-week-income" label="Week · Income" value={`$${Number(week.income || 0).toLocaleString()}`} accent />
      </div>

      {/* Sections */}
      {template.sections.map((sec) => (
        <div key={sec.id} data-testid={`section-${sec.id}`} className="hairline bg-[#0F0F0F] mb-3">
          <div className="px-6 py-4 border-b border-neutral-900 flex items-center justify-between">
            <h2 className="font-display text-sm font-bold uppercase tracking-widest">{sec.title}</h2>
            <span className="text-[10px] uppercase tracking-widest text-neutral-500">
              Weekly target × Daily / MTD / QTD / YTD tracked
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-900">
                  <th className="text-left px-6 py-3">Metric</th>
                  <th className="text-right px-3 py-3">Weekly tgt</th>
                  <th className="text-right px-3 py-3">Daily tgt</th>
                  {cols.map((c) => <th key={c} className="text-right px-3 py-3">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {sec.metrics.map((m) => {
                  const wt = Number(m.weekly_target || 0);
                  const dailyTgt = Math.round((wt / 5) * 10) / 10;
                  return (
                    <tr key={m.id} className="border-b border-neutral-900 last:border-b-0 hover:bg-[#111]">
                      <td className="px-6 py-2 font-semibold">{m.label}</td>
                      <td className="px-3 py-2 text-right text-neutral-400 tabular">{wt}</td>
                      <td className="px-3 py-2 text-right text-neutral-500 tabular">{dailyTgt}</td>
                      {cols.map((c) => {
                        const key = `${sec.id}.${m.id}.${c}`;
                        return (
                          <td key={c} className="px-2 py-1.5 w-24">
                            <Input
                              type="number"
                              min="0"
                              data-testid={`kpi-${sec.id}-${m.id}-${c}`}
                              value={(today.values?.[key] ?? 0)}
                              onChange={(e) => setVal(key, e.target.value)}
                              className="rounded-none bg-[#0A0A0A] hairline text-right tabular h-9 focus-visible:ring-1 focus-visible:ring-[#39FF14] focus-visible:border-[#39FF14]"
                            />
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {/* Income + notes + chart */}
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="hairline bg-[#0F0F0F] p-6">
          <h2 className="font-display text-sm font-bold uppercase tracking-widest mb-4">Deal income (today)</h2>
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5">Income ($)</label>
          <Input
            type="number"
            min="0"
            data-testid="kpi-income"
            value={income}
            onChange={(e) => setIncome(e.target.value)}
            className="rounded-none bg-[#0A0A0A] hairline text-2xl font-display font-bold text-[#39FF14] tabular h-14 focus-visible:ring-1 focus-visible:ring-[#39FF14] focus-visible:border-[#39FF14]"
          />
          <label className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1.5 mt-4">Notes</label>
          <Textarea
            data-testid="kpi-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Wins, blockers, next actions…"
            className="rounded-none bg-[#0A0A0A] hairline min-h-[90px]"
          />
          <Button
            data-testid="save-kpi-btn"
            onClick={save}
            disabled={saving}
            className="mt-4 w-full rounded-none bg-[#39FF14] text-black hover:bg-white text-xs uppercase tracking-widest h-11"
          >
            <Save size={14} className="mr-2" /> {saving ? "Saving…" : "Save today"}
          </Button>
        </div>
        <div className="lg:col-span-2 hairline bg-[#0F0F0F] p-6">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp size={16} className="text-[#39FF14]" />
            <h2 className="font-display text-sm font-bold uppercase tracking-widest">14-day trend</h2>
          </div>
          <div className="h-64 min-h-[16rem]" data-testid="trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={historyChart}>
                <CartesianGrid stroke="#1a1a1a" vertical={false} />
                <XAxis dataKey="entry_date" tick={CHART_TICK} tickLine={false} axisLine={CHART_AXIS_LINE} />
                <YAxis tick={CHART_TICK} tickLine={false} axisLine={CHART_AXIS_LINE} />
                <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL_STYLE} />
                <Line type="monotone" dataKey="activity" stroke="#666" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="leads" stroke="#fff" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="deals" stroke="#39FF14" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 mt-3 text-[10px] uppercase tracking-widest">
            <span className="text-neutral-500"><span className="inline-block w-2 h-2 bg-neutral-500 mr-1" />Activity</span>
            <span className="text-white"><span className="inline-block w-2 h-2 bg-white mr-1" />Leads</span>
            <span className="text-[#39FF14]"><span className="inline-block w-2 h-2 bg-[#39FF14] mr-1" />Deals</span>
          </div>
        </div>
      </div>
    </div>
  );
}
